import { useEffect, useRef, type BaseSyntheticEvent } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import type { EntryFormValues, DivisionOption, EnteredClass } from './types'

type EntryFormCardProps = {
  form: UseFormReturn<EntryFormValues>
  divisionOptions: DivisionOption[]
  classOptions: DivisionOption['classes']
  /** Classes the entrant already holds an active entry in; they stay listed but can't be chosen. */
  enteredClasses: ReadonlyMap<string, EnteredClass>
  fullyEnteredDivisionIds: ReadonlySet<string>
  hasDivisions: boolean
  hasClasses: boolean
  primarySubmitLabel: string
  onSubmit: (values: EntryFormValues, event?: BaseSyntheticEvent) => Promise<void> | void
  entryButtonDisabled: boolean
  entryButtonTooltip?: string
  permissionLoading: boolean
  canManageEntries: boolean
  entriesRolesLabel: string
  isOnline: boolean
  entryActionMessage?: string
  selectedExhibitorPresent: boolean
  autoPrintStatusMessage?: string
  autoPrintInFlight: boolean
  autoPrintEntryCode: string | null
  autoPrintError: {
    entryCode: string | null
    message: string
  } | null
  onRetryAutoPrint: () => void
  onDismissAutoPrintError: () => void
}

export function EntryFormCard({
  form,
  divisionOptions,
  classOptions,
  enteredClasses,
  fullyEnteredDivisionIds,
  hasDivisions,
  hasClasses,
  primarySubmitLabel,
  onSubmit,
  entryButtonDisabled,
  entryButtonTooltip,
  permissionLoading,
  canManageEntries,
  entriesRolesLabel,
  isOnline,
  entryActionMessage,
  selectedExhibitorPresent,
  autoPrintStatusMessage,
  autoPrintInFlight,
  autoPrintEntryCode,
  autoPrintError,
  onRetryAutoPrint,
  onDismissAutoPrintError,
}: EntryFormCardProps) {
  const allClassesEntered =
    hasClasses && classOptions.every(klass => klass.id && enteredClasses.has(klass.id))
  const primarySubmitRef = useRef<HTMLButtonElement | null>(null)
  const hasFocusedPrimaryButtonRef = useRef(false)

  useEffect(() => {
    if (!selectedExhibitorPresent) {
      hasFocusedPrimaryButtonRef.current = false
      return
    }
    if (!entryButtonDisabled && !hasFocusedPrimaryButtonRef.current) {
      primarySubmitRef.current?.focus()
      hasFocusedPrimaryButtonRef.current = true
    }
  }, [entryButtonDisabled, selectedExhibitorPresent])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Entry</CardTitle>
        <CardDescription>
          Assign a class and use the primary action to create the entry, check it in, and print the
          Exhibit ID when possible.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!permissionLoading && !canManageEntries && (
          <div className="mb-4 rounded-md border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            Creating entries requires {entriesRolesLabel}.
          </div>
        )}
        {!hasDivisions ? (
          <div className="rounded-md border border-dashed border-muted bg-muted/30 p-6 text-center text-sm text-muted-foreground">
            <p>
              This show doesn't have any divisions or classes yet. Head to Setup &gt; Classes to add
              them before intaking entries.
            </p>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((values, event) => onSubmit(values, event))}
              className="space-y-4"
            >
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="divisionId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Division</FormLabel>
                      <FormControl>
                        <select
                          {...field}
                          disabled={!hasDivisions}
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm disabled:opacity-50"
                        >
                          {divisionOptions.map(division => (
                            <option key={division.id} value={division.id}>
                              {division.name}
                              {fullyEnteredDivisionIds.has(division.id) ? ' (all entered)' : ''}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="classId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Class</FormLabel>
                      <FormControl>
                        <select
                          {...field}
                          disabled={!hasClasses}
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm disabled:opacity-50"
                        >
                          {allClassesEntered && (
                            <option value="" disabled>
                              No classes left to enter
                            </option>
                          )}
                          {classOptions.map(klass => {
                            const entered = klass.id ? enteredClasses.get(klass.id) : undefined
                            return (
                              <option
                                key={klass.id ?? klass.code}
                                value={klass.id ?? klass.code}
                                disabled={Boolean(entered)}
                              >
                                {klass.name}
                                {entered
                                  ? entered.pending
                                    ? ' — already entered (waiting to sync)'
                                    : ` — already entered (Exhibit ID ${entered.entryCode})`
                                  : ''}
                              </option>
                            )
                          })}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (optional)</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Any notes to help secretaries" rows={3} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <CardFooter className="px-0 flex flex-col gap-3 items-start">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-start">
                  <Button
                    ref={primarySubmitRef}
                    type="submit"
                    data-action="create-and-check-in"
                    disabled={entryButtonDisabled}
                    title={entryButtonTooltip}
                  >
                    {primarySubmitLabel}
                  </Button>
                  <Button
                    type="submit"
                    variant="secondary"
                    data-action="create-only"
                    disabled={entryButtonDisabled}
                    title={entryButtonTooltip}
                  >
                    Create without check-in
                  </Button>
                </div>
                {!isOnline && (
                  <Badge
                    variant="secondary"
                    className="border-amber-200 bg-amber-100 text-amber-900"
                    data-testid="offline-entry-badge"
                  >
                    Offline: entries store locally &amp; sync later
                  </Badge>
                )}
                {entryActionMessage && (
                  <p className="text-xs text-muted-foreground">{entryActionMessage}</p>
                )}
                {selectedExhibitorPresent && autoPrintStatusMessage && (
                  <p className="text-xs text-muted-foreground">{autoPrintStatusMessage}</p>
                )}
                {autoPrintInFlight && autoPrintEntryCode && (
                  <p className="text-xs text-muted-foreground">
                    Printing Exhibit ID <span className="font-mono">{autoPrintEntryCode}</span>…
                  </p>
                )}
                {autoPrintError && (
                  <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                    <p>
                      Auto-print failed for Exhibit ID{' '}
                      <span className="font-mono">{autoPrintError.entryCode ?? '—'}</span>:{' '}
                      {autoPrintError.message}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={onRetryAutoPrint}>
                        Retry print
                      </Button>
                      <Button size="sm" variant="ghost" onClick={onDismissAutoPrintError}>
                        Dismiss
                      </Button>
                    </div>
                  </div>
                )}
              </CardFooter>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  )
}
